import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "../lib/site";

const HEADLINE = ["Strength,", "delivered", "to your door."];

const scrollTo = (id) => {
  const t = document.getElementById(id);
  if (!t) return;
  if (window.__lenis) window.__lenis.scrollTo(t, { offset: -80 });
  else t.scrollIntoView({ behavior: "smooth" });
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-[#FAF7F2]">
      {/* parallax image panel */}
      <motion.div
        style={{ y, scale }}
        className="hidden md:block absolute right-0 top-0 h-full md:w-[46%] overflow-hidden md:clip-arch"
      >
        <div className="grain frame-inset relative h-full w-full">
          <img src={IMAGES.hero} alt="Private Pilates at home" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#18181B]/35 via-transparent to-transparent" />
          <span className="absolute bottom-10 right-8 z-10 label text-[#FAF7F2]/85 text-[0.6rem] [writing-mode:vertical-rl] rotate-180">
            Est. Hampstead · London
          </span>
        </div>
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-14 md:pr-[46%] pt-32 md:pt-44 pb-16 md:min-h-screen flex flex-col md:justify-center">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="eyebrow label text-[#2D4A3E] mb-7"
          data-testid="hero-eyebrow"
        >
          Private at-home Pilates · Hampstead, London
        </motion.p>

        <h1 data-testid="hero-headline" className="font-display text-5xl sm:text-6xl lg:text-[5.6rem] leading-[0.98] tracking-tight text-[#1C1917] max-w-3xl">
          {HEADLINE.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.3 + i * 0.13, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 2 ? (
                  <span className="italic text-[#2D4A3E]">{line}</span>
                ) : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 max-w-md text-lg text-[#57534E] leading-relaxed"
          data-testid="hero-subtext"
        >
          One-to-one sessions, brought into the calm of your own home. Real progress is quiet, consistent and built around you.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-btn"
            onClick={() => scrollTo("booking")}
            className="btn-lux rounded-full bg-[#18181B] text-[#FAF7F2] px-8 py-4 label hover:bg-[#2D4A3E]"
          >
            Book Your First Session
          </button>
          <button
            data-testid="hero-pricing-btn"
            onClick={() => scrollTo("pricing")}
            className="btn-lux rounded-full border border-[#1C1917]/25 text-[#1C1917] px-8 py-4 label hover:border-[#2D4A3E] hover:text-[#2D4A3E]"
          >
            View Pricing
          </button>
        </motion.div>

        {/* floating stat badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.25, duration: 0.7 }}
          style={{ opacity: fade }}
          data-testid="hero-stat-badge"
          className="flex md:hidden lg:flex flex-col w-fit mt-10 md:mt-14 bg-[#FAF7F2]/90 backdrop-blur-md border border-[#1C1917]/10 rounded-2xl px-7 py-5"
        >
          <p className="font-display text-4xl text-[#2D4A3E]">55<span className="text-lg align-top">min</span></p>
          <p className="label text-[#78716C] mt-1">of undivided focus</p>
        </motion.div>

        {/* mobile photo — stacks below content, never behind the text */}
        <div data-testid="hero-mobile-image" className="md:hidden mt-10 -mx-5 sm:-mx-8">
          <div className="grain relative h-72 sm:h-96 w-full overflow-hidden">
            <img src={IMAGES.hero} alt="Private Pilates at home" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#18181B]/25 via-transparent to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}
